import React, { useContext, useEffect, useState } from 'react';
import { FormProvider, UploadSingleFile, NextImage } from '@sdf/base';
import { Styles } from '@t2c/shared-fe';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import { useBrollPage } from '../../../../t2c-fe/hooks/broll/useBrollPage';
import { BrollClipProccessingModal } from '../../broll/BrollClipProccessingModal';
import {
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { CreditDialog } from '../../create-video/CreditDialog';
import { NumGenerationsEnum, AiVideoModelEnum } from '../../../utils/constant';
import { UploadImage } from '../../broll/UploadImage';
import { UploadVideo } from '../../broll/UploadVideo';
import { useDialog } from 'apps/t2c-fe/hooks';
import { EXTENDED_ASPECT_RATIO_OPTIONS } from 'apps/t2c-fe/utils';
import { AspectRatios } from '@t2c/ai-video-editor';
import { BrowseBRollModal } from '../../edit-video/modals/BrowseBRoll/BrowseBRollModal';
import { SelectAvatarModal } from '../../talking-face/SelectAvatar';
import { LoadingButton } from '@mui/lab';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { CreditContext } from 'apps/t2c-fe/context/CreditProvider';
import { PremiumBadge } from '../../broll/BrollPage';
import { RenderGateModal } from '../../common/RenderGateModal';
import { useRouter } from 'next/router';
import ImageEditor from '../../common/ImageEditor';

interface Props {
  refetch: () => {};
}
export const BrollClipWorkBench: React.FC<Props> = ({ refetch }) => {
  const {
    uploadURL,
    isImage,
    setIsImage,
    maxCharLimit,
    setMaxCharLimit,
    supportedFormats,
    clipGenerationData,
    setClipGenerationData,
    handleDataChange,
    handleClipGenerate,
    handleAudioToggle,
    handleEnhancePromptToggle,
    isCreditDialogOpen,
    closeCreditDialog,
    isFetchingRequiredCredits,
    isCheckingEligibility,
    requiredCreditsData,
    handleConfirmGeneration,
    clipGenerationLoading,
    isProccessingModalOpen,
    closeProccessingModal,
    newGenerationData,
    handleUpgrade,
    abortController,
    abortTailController,
    setUploadURL,
    setUploadTailURL,
    handleMediaSelect,
    uploadTailURL,
    setIsSelectedTail,
    handleUpdateStartImageId,
    handleUpdateEndImageId,
    selectedCharacterImage,
    setSelectedCharacterImage,
    handleCharacterSelect,
    isSelectCharacterDialog,
    openSelectCharacterDialog,
    closeSelectCharacterDialog,
    handleBodyControlToggle,
    handlePromptEnhance,
    renderGateModalMode,
    openRenderGateModal,
    setRenderGateModalMode,
    setOpenRenderGateModal,
    isEnhancing,
    preValidator,
    isTailuploading,
    setIsTailUploading,
    isUploading,
    setIsUploading,
    applyModelDefaults,
    showEditor,
    setShowEditor,
    imageFile,
    setImageFile,
    errorMsg,
    setErrorMsg,
    handleMenuClick,
  } = useBrollPage({ refetchBrollClips: refetch });
  const { push } = useRouter();
  const { modelDetails, userPlanData, agentPlanData } = useContext(CreditContext);

  const theme = useTheme();
  const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    isDialogOpen: isOpenBrowseBRollModal,
    openDialog: openBrowseBRollModal,
    closeDialog: CloseBrowseBRollModal,
  } = useDialog();

  const handleClickStartImage = () => {
    setIsImage(true);
    setIsSelectedTail(false);
    openBrowseBRollModal();
  };
  const handleClickVideo = () => {
    setIsImage(false);
    setIsSelectedTail(false);
    openBrowseBRollModal();
  };
  const handleClickEndImage = () => {
    setIsImage(true);
    setIsSelectedTail(true);
    openBrowseBRollModal();
  };

  return (
    <Grid container gap={3}>
      {clipGenerationData.model === AiVideoModelEnum.RUNWAY_ACT_TWO ? (
        <Grid item xs={12}>
          <UploadVideo
            uploadURL={uploadURL}
            abortController={abortController}
            openBrowseBRollModal={handleClickVideo}
            handleUpdateImage={handleUpdateStartImageId}
            uploading={isUploading}
            setUploading={setIsUploading}
            limit={modelDetails[clipGenerationData.model].maxImageSize}
            isFullPage
          />
        </Grid>
      ) : (
        <Grid item xs={12}>
          <Typography sx={styles.heading}>
            What type of AI video clip would you like to create?
          </Typography>
          <Box sx={{ position: 'relative' }}>
            <TextField
              placeholder={
                clipGenerationData.model == AiVideoModelEnum.KLING_V3_MOTION_CONTROL
                  ? 'E.g., Smooth cinematic motion, slow dolly forward, stable camera, subtle parallax, realistic depth of field, no jitter or warping. (Optional)'
                  : 'E.g., Vibrant cityscape with lit skyscrapers, busy streets, and glowing neon signs.'
              }
              size="medium"
              type="text"
              name="prompt"
              multiline
              inputProps={{ maxLength: maxCharLimit }}
              rows={isExtraSmall ? 2 : 7}
              sx={styles.textField}
              onChange={(e) => handleDataChange('prompt', e.target.value?.slice(0, maxCharLimit))}
              value={clipGenerationData.prompt}
              fullWidth
            />
            <Tooltip title="Enhance prompt with AI" placement="top">
              <IconButton
                size="small"
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  right: 4,
                  zIndex: 10,
                }}
                disabled={!clipGenerationData.prompt.trim() || isEnhancing}
                onClick={handlePromptEnhance}
              >
                {isEnhancing ? (
                  <CircularProgress size={20} />
                ) : (
                  <NextImage
                    src={'/assets/icons/create-video_default.svg'}
                    alt=""
                    width={24}
                    height={24}
                  />
                )}
              </IconButton>
            </Tooltip>
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: (theme) => theme.palette.text.lighterText2, pl: '2px', pt: '2px' }}
            >
              {clipGenerationData?.prompt?.length || 0}/{maxCharLimit} characters
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mt: 1 }}>
              {modelDetails[clipGenerationData.model].showEnhancePrompt && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    mt: 1,
                  }}
                >
                  <Typography sx={{ ...styles.label, mt: 0.5 }}>Enhance Prompt</Typography>
                  <Switch
                    checked={clipGenerationData.enhancePrompt}
                    onChange={handleEnhancePromptToggle}
                  />
                </Box>
              )}
              {modelDetails[clipGenerationData.model].showAudio && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    mt: 1,
                  }}
                >
                  <Typography sx={{ ...styles.label, mt: 0.5 }}>Generate Audio</Typography>
                  <Switch checked={clipGenerationData.generateAudio} onChange={handleAudioToggle} />
                </Box>
              )}
            </Box>
          </Box>
        </Grid>
      )}
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Stack>
              <Typography sx={styles.label}>Enter Name</Typography>
              <TextField
                placeholder="Type name here"
                size="medium"
                type="text"
                name="name"
                sx={styles.textField}
                value={clipGenerationData.name}
                onChange={(e) => handleDataChange('name', e.target.value)}
              />
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box flex={1}>
              <Typography sx={styles.label}>Model</Typography>
              <Select
                labelId="model-select-label"
                value={clipGenerationData.model}
                onChange={(e) => applyModelDefaults(e.target.value as AiVideoModelEnum)}
                sx={{
                  width: '100%',
                  backgroundColor: (theme) => theme.palette.background.secondary,
                }}
                MenuProps={{
                  PaperProps: {
                    sx: styles.dropdownProps,
                  },
                }}
                renderValue={(selected) => (
                  <Box>
                    {selected} {modelDetails[selected].isPremium && <PremiumBadge />}
                  </Box>
                )}
              >
                {Object.values(AiVideoModelEnum).map((output) => (
                  <MenuItem key={output} value={output} onClick={() => handleMenuClick(output)}>
                    <Box>
                      <Box>
                        {output} {modelDetails[output].isPremium && <PremiumBadge />}
                      </Box>
                      {output === AiVideoModelEnum.RUNWAY_ACT_TWO && (
                        <NextImage
                          src="/assets/RunwayLogo.svg"
                          alt="Runway"
                          width={100}
                          height={10}
                        />
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Stack direction={'row'} spacing={1}>
              <Box flex={1}>
                <Typography sx={styles.label}>Generations</Typography>
                <Select
                  labelId="numGeneration-select-label"
                  value={clipGenerationData.numGenerations}
                  onChange={(e) =>
                    handleDataChange('numGenerations', e.target.value as NumGenerationsEnum)
                  }
                  sx={{
                    width: '100%',
                    backgroundColor: (theme) => theme.palette.background.secondary,
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: styles.dropdownProps,
                    },
                  }}
                >
                  {Object.values(NumGenerationsEnum).map((output) => (
                    <MenuItem key={output} value={output}>
                      {output}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              <Box flex={1}>
                <Typography sx={styles.label}>Duration(in sec)</Typography>
                <Select
                  labelId="duration-select-label"
                  value={
                    clipGenerationData.model === AiVideoModelEnum.KLING_V3_MOTION_CONTROL ||
                    clipGenerationData.model === AiVideoModelEnum.RUNWAY_ACT_TWO
                      ? 'Video Duration'
                      : clipGenerationData.duration
                  }
                  onChange={(e) => handleDataChange('duration', e.target.value.toString())}
                  sx={{
                    width: '100%',
                    backgroundColor: (theme) => theme.palette.background.secondary,
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: styles.dropdownProps,
                    },
                  }}
                >
                  {clipGenerationData.model === AiVideoModelEnum.KLING_V3_MOTION_CONTROL ||
                  clipGenerationData.model === AiVideoModelEnum.RUNWAY_ACT_TWO ? (
                    <MenuItem value="Video Duration">Video Duration</MenuItem>
                  ) : (
                    modelDetails[clipGenerationData.model].durationPerModel.map((output) => (
                      <MenuItem key={output} value={output}>
                        {output}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Stack direction={{ lg: 'column', sm: 'column', xl: 'row', xs: 'row' }} spacing={1}>
              {modelDetails[clipGenerationData.model].orientationPerModel.length > 0 && (
                <Box flex={1}>
                  <Typography sx={styles.label}>Orientation</Typography>
                  <Select
                    labelId="orientation-select-label"
                    value={clipGenerationData.orientation}
                    onChange={(e) => handleDataChange('orientation', e.target.value)}
                    sx={{
                      width: '100%',
                      backgroundColor: (theme) => theme.palette.background.secondary,
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: styles.dropdownProps,
                      },
                    }}
                  >
                    {EXTENDED_ASPECT_RATIO_OPTIONS.filter((option) =>
                      modelDetails[clipGenerationData.model].orientationPerModel.includes(
                        option.value
                      )
                    ).map(({ id, value, icon, label, name }) => (
                      <MenuItem key={id} value={value} sx={styles.menuItem}>
                        <Stack direction="row" alignItems="center" gap={1}>
                          <Box sx={{ width: 35, display: 'flex', justifyContent: 'center' }}>
                            {icon}
                          </Box>
                          <Stack direction="row" alignItems="center" gap={1}>
                            <Typography variant="body2" sx={styles.menuLabel}>
                              {label} {name}
                            </Typography>
                          </Stack>
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              )}

              {modelDetails[clipGenerationData.model].resolutionPerModel.length > 0 && (
                <Box flex={1}>
                  <Typography sx={styles.label}>Resolution</Typography>
                  <Select
                    labelId="resolution-select-label"
                    value={clipGenerationData.resolution}
                    onChange={(e) => handleDataChange('resolution', e.target.value)}
                    sx={{
                      width: '100%',
                      backgroundColor: (theme) => theme.palette.background.secondary,
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: styles.dropdownProps,
                      },
                    }}
                  >
                    {modelDetails[clipGenerationData.model].resolutionPerModel.map((output) => (
                      <MenuItem key={output} value={output}>
                        {output}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              )}

              {clipGenerationData.model === AiVideoModelEnum.KLING_V3_MOTION_CONTROL && (
                <>
                  <Box flex={2}>
                    {/* Label + info tooltip */}
                    <Stack direction="row" alignItems="center" gap={0.5} mb={0.5}>
                      <Typography sx={styles.label}>Character Orientation</Typography>

                      <Tooltip
                        title={
                          <Box sx={{ p: 1 }}>
                            <Typography fontSize={12}>
                              <b>Image:</b> The character's orientation matches the reference image,
                              while movements and expressions still follow the video motion
                              reference. Camera motion and other elements can be customized via
                              prompts.
                            </Typography>
                            <Typography fontSize={12} mt={0.5}>
                              <b>Video:</b> The character's orientation, movements, expressions, and
                              camera motion follow the video motion reference. Other details can be
                              adjusted using prompts.
                            </Typography>
                          </Box>
                        }
                        enterTouchDelay={0}
                        leaveTouchDelay={3000}
                        componentsProps={{
                          tooltip: {
                            sx: {
                              borderRadius: 2,
                              maxWidth: 260,
                            },
                          },
                        }}
                      >
                        <InfoOutlinedIcon
                          sx={{
                            fontSize: 16,
                            color: '#60A5FA',
                            cursor: 'pointer',
                          }}
                        />
                      </Tooltip>
                    </Stack>

                    {/* Select */}
                    <Select
                      value={clipGenerationData.bodyControl ? 'video' : 'image'}
                      onChange={(e) =>
                        handleBodyControlToggle({
                          target: {
                            checked: e.target.value === 'video',
                          },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                      sx={{
                        width: '100%',
                        backgroundColor: (theme) => theme.palette.background.secondary,
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: styles.dropdownProps,
                        },
                      }}
                    >
                      <MenuItem value="image" sx={styles.menuItem}>
                        Image
                      </MenuItem>

                      <MenuItem value="video" sx={styles.menuItem}>
                        Video
                      </MenuItem>
                    </Select>
                  </Box>

                  <Box flex={1}>
                    <Typography sx={{ ...styles.label, mt: 0.5 }}>Keep Audio</Typography>
                    <Switch
                      checked={clipGenerationData.generateAudio}
                      onChange={handleAudioToggle}
                    />
                  </Box>
                </>
              )}

              {clipGenerationData.model === AiVideoModelEnum.KLING_V3_PRO && (
                <Box flex={1}>
                  <Typography sx={styles.label}>Multi Shot Generation</Typography>
                  <Select
                    labelId="multiShot-select-label"
                    value={clipGenerationData.multiShot}
                    onChange={(e) => handleDataChange('multiShot', e.target.value.toString())}
                    sx={{
                      width: '100%',
                      backgroundColor: (theme) => theme.palette.background.secondary,
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: styles.dropdownProps,
                      },
                    }}
                  >
                    {modelDetails[clipGenerationData.model].mutliShot.map((output) => (
                      <MenuItem key={output} value={output.toString()}>
                        {output}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} order={{ xs: 1, sm: 1 }}>
            <Box mt={1.5} sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
              {modelDetails[clipGenerationData.model]?.showVideo && (
                <Box>
                  <Typography sx={styles.text}>Reference Video*</Typography>
                  <UploadVideo
                    uploadURL={uploadURL}
                    abortController={abortController}
                    openBrowseBRollModal={handleClickVideo}
                    handleUpdateImage={handleUpdateStartImageId}
                    uploading={isUploading}
                    setUploading={setIsUploading}
                    limit={modelDetails[clipGenerationData.model].maxImageSize}
                    preValidator={preValidator}
                  />
                </Box>
              )}
              {modelDetails[clipGenerationData.model].showUpload && (
                <Box>
                  <Typography sx={styles.text}>
                    Start Image{' '}
                    {modelDetails[clipGenerationData.model].isStartImageRequired ? '*' : ''}
                  </Typography>
                  <UploadImage
                    uploadURL={uploadURL}
                    abortController={abortController}
                    openBrowseBRollModal={handleClickStartImage}
                    handleUpdateImage={handleUpdateStartImageId}
                    uploading={isUploading}
                    setUploading={setIsUploading}
                    limit={modelDetails[clipGenerationData.model].maxImageSize}
                    preValidator={preValidator}
                    orientation={clipGenerationData.orientation}
                    resolution={clipGenerationData.resolution}
                    imageFile={imageFile}
                    setImageFile={setImageFile}
                    errorMsg={errorMsg}
                    setErrorMsg={setErrorMsg}
                    setIsImage={setIsImage}
                  />
                </Box>
              )}
              {modelDetails[clipGenerationData.model].showTailUpload && (
                <Box>
                  <Typography sx={styles.text}>
                    {clipGenerationData.model === AiVideoModelEnum.KLING_V3_MOTION_CONTROL
                      ? 'Reference Image*'
                      : 'End Image'}
                  </Typography>
                  <UploadImage
                    uploadURL={uploadTailURL}
                    abortController={abortTailController}
                    openBrowseBRollModal={handleClickEndImage}
                    handleUpdateImage={handleUpdateEndImageId}
                    uploading={isTailuploading}
                    setUploading={setIsTailUploading}
                    limit={modelDetails[clipGenerationData.model].maxImageSize}
                    preValidator={preValidator}
                    orientation={clipGenerationData.orientation}
                    resolution={clipGenerationData.resolution}
                    imageFile={imageFile}
                    setImageFile={setImageFile}
                    errorMsg={errorMsg}
                    setErrorMsg={setErrorMsg}
                    setIsImage={setIsImage}
                  />
                </Box>
              )}
              {modelDetails[clipGenerationData.model].showCharacter && (
                <Box>
                  {selectedCharacterImage && (
                    <Typography sx={styles.label}>Selected Character</Typography>
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {selectedCharacterImage && (
                      <Box sx={styles.uploader}>
                        <NextImage src={selectedCharacterImage} height={100} width={100} alt="" />
                      </Box>
                    )}

                    <Box sx={styles.CharacterSelectBtn} onClick={openSelectCharacterDialog}>
                      <NextImage
                        src={'/assets/icons/Avatars.svg'}
                        alt=""
                        width={49}
                        height={49}
                        style={{ height: 'auto' }}
                      />
                      <Typography fontWeight={500}>
                        {clipGenerationData.uploadTailId
                          ? 'Choose other Character'
                          : 'Choose Character'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
              {modelDetails[clipGenerationData.model].showBodyControl && (
                <Box>
                  <Typography sx={{ ...styles.label, mt: 0.5 }}>Control Body</Typography>
                  <Switch
                    checked={clipGenerationData.bodyControl}
                    onChange={handleBodyControlToggle}
                  />
                </Box>
              )}
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            order={{ xs: 1, sm: 2 }}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end', // push content to bottom vertically
            }}
          >
            <Box mt={2}>
              <LoadingButton
                variant="contained"
                fullWidth
                sx={styles.createBtn}
                endIcon={<NextImage src={'/assets/icons/Ai.svg'} alt="" width={24} height={24} />}
                onClick={handleClipGenerate}
                disabled={
                  isFetchingRequiredCredits || clipGenerationLoading || isCheckingEligibility
                }
                loading={isUploading || isTailuploading}
              >
                Create
              </LoadingButton>
            </Box>
          </Grid>
        </Grid>
      </Grid>
      {/* <BrollClipProccessingModal data={} /> */}

      {openRenderGateModal && (
        <RenderGateModal
          open={openRenderGateModal}
          mode={renderGateModalMode}
          onClose={() => {
            setOpenRenderGateModal(false);
            setRenderGateModalMode('processing');
          }}
          userPlanData={userPlanData}
          agentPlanData={agentPlanData}
          push={push}
        />
      )}

      <CreditDialog
        isOpen={isCreditDialogOpen}
        onClose={closeCreditDialog}
        isFetchingCreditDetail={isFetchingRequiredCredits}
        onConfirm={handleConfirmGeneration}
        creditDetail={{
          availableCredit: requiredCreditsData?.availableCredits,
          creditNeeded: requiredCreditsData?.requiredCredits,
          creditsInUse: requiredCreditsData?.creditsInUse,
        }}
        isLoading={clipGenerationLoading}
        onUpgrade={handleUpgrade}
        enoughCreditConfirmBtnTitle={'Generate'}
        enoughCreditModalTitle="Confirm Generation"
        upgradeButtonTitle="Upgrade"
      />

      {isProccessingModalOpen && (
        <BrollClipProccessingModal
          handleCloseDialog={closeProccessingModal}
          shouldOpenDialog={isProccessingModalOpen}
          data={newGenerationData}
        />
      )}

      {isOpenBrowseBRollModal && (
        <BrowseBRollModal
          handleImageSelect={handleMediaSelect}
          handleCloseDialog={CloseBrowseBRollModal}
          shouldOpenDialog={isOpenBrowseBRollModal}
          isImage={isImage}
          isBrollPage={true}
          showAllmodels={
            clipGenerationData.model === AiVideoModelEnum.RUNWAY_ACT_TWO ||
            clipGenerationData.model === AiVideoModelEnum.KLING_V3_MOTION_CONTROL
          }
        />
      )}

      {isSelectCharacterDialog && (
        <SelectAvatarModal
          shouldOpenDialog={isSelectCharacterDialog}
          handleCloseDialog={closeSelectCharacterDialog}
          handleOnClick={handleCharacterSelect}
        />
      )}

      {showEditor && (
        <ImageEditor
          open={showEditor}
          onClose={() => {
            setShowEditor(false);
            setErrorMsg('');
          }}
          imageFile={imageFile}
          handleFileSelect={handleMediaSelect}
          orientation={clipGenerationData.orientation}
          resolution={clipGenerationData.resolution}
          fromBrollUpload={true}
          errorMsg={errorMsg}
        />
      )}
    </Grid>
  );
};

const styles: Styles = {
  textField: {
    '& .MuiOutlinedInput-root': {
      border: '1px solid',
      borderRadius: '6px',
      borderColor: (theme) => theme.palette.background.ternary,
      backgroundColor: (theme) => theme.palette.background.secondary,
      '&:hover': {
        borderColor: (theme) => theme.palette.background.activeBorder,
      },
    },
    '& .MuiOutlinedInput-input::placeholder': {
      color: (theme) => theme.palette.text.placeholder,
    },
  },
  heading: {
    fontSize: 14,
    fontWeight: 500,
    color: (theme) => theme.palette.text.primary,
    marginBottom: '5px',
    marginTop: '5px',
  },
  label: {
    fontSize: 14,
    fontWeight: 400,
    color: (theme) => theme.palette.text.primary,
    marginBottom: '5px',
  },
  uploadText: {
    textAlign: 'center',
    color: (theme) => theme.palette.primary.main,
    fontWeight: 600,
  },
  uploadContainer: {
    height: 100,
    width: 120,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
    marginTop: '10px',
  },
  createBtn: {
    py: 3,
    fontSize: 16,
    color: (theme) => theme.palette.text.primary,
    boxShadow: 'none',
  },
  uploading: {
    position: 'relative',
    borderRadius: '10px',
    width: '210px',
  },
  dropdownProps: {
    maxHeight: '175px',
    maxWidth: 200,
    bgcolor: (theme) => theme.palette.background.secondary,
    '& .MuiMenuItem-root': {
      backgroundColor: (theme) => theme.palette.background.secondary,
      '&:hover': {
        backgroundColor: (theme) => theme.palette.background.activeCard,
      },
    },
  },
  uploader: {
    mr: 3,
    height: 70,
    width: 70,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    textAlign: 'center',
    borderRadius: '6px',
    flexDirection: 'column',
    borderColor: (theme) => theme.palette.background.border,
    backgroundColor: (theme) => theme.palette.background.primary,
    cursor: 'pointer',
  },
  CharacterSelectBtn: {
    p: '10px',
    display: 'flex',
    gap: 1,
    borderWidth: 1,
    borderRadius: 56,
    borderColor: (theme) => theme.palette.background.border,
    cursor: 'pointer',
  },
};
